import 'firebase/firestore';
import { auth } from '../../firebase';
import { useSelector, useDispatch } from 'react-redux';
import { setUser, initialState } from 'store/auth/userSlice';
import { onSignInSuccess, onSignOutSuccess } from 'store/auth/sessionSlice';
import appConfig from 'configs/app.config';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, gql, useLazyQuery } from '@apollo/client';
import { apiForgotPassword } from 'services/AuthService';
import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import authRoute from 'configs/routes.config/authRoute';
import { toast, Notification } from 'components/ui';
import { useAutoLogout } from './useAutoLogout';
import { clearCheckoutUrl, getCheckoutUrl } from 'utils/checkoutUrl';
import { trackCompleteRegistration, getAttributionParams } from 'utils/analytics';
import { pickAttribution } from 'utils/lifecycleEvents';

function useAuth() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { token, signedIn } = useSelector((state) => state.auth.session);
  const currentUser = useSelector((state) => state.auth.user);

  const queryParams = new URLSearchParams(window.location.search);
  const redirectUrl = queryParams.get('redirectUrl');
  const doctor_id = queryParams.get('doctor_id');
  const specialty = queryParams.get('specialty');
  const date = queryParams.get('date');
  const type = queryParams.get('type');
  const appointmentUrl = `${redirectUrl}&specialty=${specialty}&date=${date}&type=${type}`;

  const authUrl = redirectUrl
    ? redirectUrl.includes('/checkout')
      ? appointmentUrl
      : redirectUrl
    : '/';

  const [apiSignIn] = useMutation(SIGN_IN);
  const [apiSignUp] = useMutation(SIGN_UP);
  const { data, loading, error } = useQuery(GET_USER, {
    skip: !localStorage.getItem('token'),
    variables: {
      email: localStorage.getItem('email'),
    },
  });
  const [createDoctor] = useMutation(CREATE_DOCTOR);
  const [createPacient] = useMutation(CREATE_PACIENT);
  const [getUser] = useLazyQuery(GET_USER);

  // Declarar signOut antes de usar no useAutoLogout
  const signOut = useCallback(
    async (route) => {
      // Obter checkoutUrl antes de limpar (se não foi passado como parâmetro)
      const checkoutUrl = route || getCheckoutUrl();
      
      await auth?.signOut();
      localStorage.removeItem('token');

      // Limpar URL de checkout e timestamp ao fazer signout (apenas se não estamos redirecionando para checkout)
      if (!checkoutUrl || !checkoutUrl.includes('/checkout')) {
        clearCheckoutUrl();
      }

      // Limpar o estado de forma segura para evitar referências circulares
      try {
        dispatch(onSignOutSuccess());

        // Reset direto para initialState para evitar referências circulares
        dispatch(setUser(initialState));
      } catch (error) {
        console.warn('Erro ao limpar estado durante signout:', error);
        // Fallback para initialState se houver erro
        dispatch(setUser(initialState));
      }

      // Navegar e recarregar a página
      // Se temos checkoutUrl, usar ele; caso contrário, usar route ou tourPath
      const redirectRoute = checkoutUrl || route || appConfig.tourPath;
      navigate(redirectRoute);

      // Usar setTimeout para garantir que o dispatch seja processado antes do reload
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
    [dispatch, navigate]
  );

  // Novo sistema de auto-logout com aviso prévio
  const { extendSession, showWarningDialog, setShowWarningDialog } = useAutoLogout(
    signOut,
    token && signedIn
  );

  const configUser = useCallback((user) => {
    console.log('🔧 configUser chamado com:', user);

    dispatch(
      setUser({
        ...user,
        // Preservar image_url se existir, caso contrário usar avatar vazio
        avatar: user?.image_url || user?.avatar || '',
        // Garantir que image_url seja preservado
        image_url: user?.image_url || user?.avatar || '',
        authority: user.role || ['user'],
        userName: user.firstname,
        email: user.email,
      })
    );

    const token = localStorage.getItem('token');
    dispatch(onSignInSuccess(token));

    // Verificar checkoutUrl primeiro (maior prioridade)
    const checkoutUrl = getCheckoutUrl();
    console.log('🔍 checkoutUrl encontrado:', checkoutUrl);

    if (checkoutUrl) {
      console.log('✅ Redirecionando para checkoutUrl:', checkoutUrl);
      navigate(checkoutUrl);
      // Não remover checkoutUrl aqui - manter para permitir navegação de volta
      // A URL será limpa apenas quando o checkout for concluído com sucesso
      return; // Importante: sair da função para não executar outros redirecionamentos
    }

    // Verificar doctorUrl como segunda prioridade
    const doctorUrl = localStorage.getItem('doctorUrl');
    console.log('🔍 doctorUrl encontrado:', doctorUrl);

    if (doctorUrl) {
      console.log('✅ Redirecionando para doctorUrl:', doctorUrl);
      window.location.href = doctorUrl;
      localStorage.removeItem('doctorUrl');
      return; // Importante: sair da função para não executar outros redirecionamentos
    }

    // Redirecionamento padrão para consultas
    const currentPath = window.location.pathname;
    const isAdminRoute = currentPath.startsWith('/admin');

    console.log(
      '🔍 Redirecionamento padrão - currentPath:',
      currentPath,
      'isAdminRoute:',
      isAdminRoute
    );

    // Se não estiver em uma rota de admin, redireciona para consultas
    if (!isAdminRoute) {
      console.log('✅ Redirecionando para consultas:', appConfig.authenticatedEntryPath);
      navigate(appConfig.authenticatedEntryPath);
    }
  }, []);

  // Atualizar dados do usuário quando a query GET_USER retornar dados
  useEffect(() => {
    if (data?.users?.length > 0 && token && signedIn) {
      const user = data.users[0];
      // Atualizar usuário no Redux para garantir que image_url e outros campos estejam atualizados
      // Comparar apenas campos essenciais para evitar loops infinitos
      if (!currentUser?.id || currentUser?.id !== user.id || currentUser?.image_url !== user.image_url) {
        configUser(user);
      }
    }
  }, [data, token, signedIn]);

  // Removido o useEffect que causava chamada duplicada de configUser
  // A função configUser já é chamada na função signIn

  // useEffect(() => {
  //   if (
  //     token &&
  //     signedIn &&
  //     pathname !== '/' &&
  //     authRoute.find((route) => route.path.includes(pathname))
  //   ) {
  //     navigate(authUrl);
  //     if (authUrl === '/') {
  //       signOut();
  //     }
  //   }
  // }, [pathname, token, signedIn]);

  const fetchUserInformation = async (email) => {
    // Usar o endpoint de proxy do service ao invés do Hasura diretamente
    return fetch(`${process.env.REACT_APP_SERVICE_API}/api/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Removido o x-hasura-admin-secret - agora o service gerencia isso
      },
      body: JSON.stringify({
        query: `query getUser($email: String!) {
          users(where: { email: { _eq: $email } }) {
            firebase_uid
            id
            email
            firstname
            lastname
            document
            role
            image_url
            telephone
            telephone_ddi
            gender
            gender_identity
            pronouns
            date_of_birth
            zipcode
            address
            number
            complement
            country
            city
            region
          }
        }`,
        variables: {
          email,
        },
      }),
    })
      .then((res) => res.json())
      .catch((err) => console.error(err));
  };

  const signIn = async (values) => {
    try {
      const resp = await apiSignIn({
        variables: {
          input: { ...values, email: values?.email?.toLowerCase() },
        },
      });
      if (resp?.data?.signIn) {
        const { access_token: token } = resp.data.signIn;
        localStorage.setItem('token', token);
        localStorage.setItem('email', values.email?.toLowerCase());

        const userResponse = await fetchUserInformation(values.email?.toLowerCase());
        if (userResponse?.data?.users?.length > 0) {
          const user = userResponse?.data?.users[0];
          configUser(user);
        }
        return { success: true };
      }
      // Se não tem data.signIn, é um erro
      return { success: false, error: 'Email ou senha inválidos' };
    } catch (errors) {
      console.error('Erro no signIn:', errors);
      // Retorna erro genérico - simples e funcional
      return { success: false, error: 'Email ou senha inválidos. Verifique suas credenciais.' };
    }
  };

  const signUp = async ({ type, initial_dependent, verification_token, ...rest }, { setMessage }) => {
    try {
      const signUpInputKeys = [
        'email',
        'password',
        'telephone',
        'telephone_ddi',
        'firstname',
        'lastname',
        'address',
        'number',
        'complement',
        'zipcode',
        'city',
        'region',
        'country',
        'document',
        'date_of_birth',
        'gender',
        'gender_identity',
        'pronouns',
        'crm',
        'neighborhood',
        'terms_accepted',
      ];
      const input = {};
      signUpInputKeys.forEach((key) => {
        if (rest[key] !== undefined) {
          input[key] = rest[key];
        }
      });
      input.email = rest?.email?.toLowerCase();
      input.country = 'BR';
      input.role = type === 'doctor' ? 'doctor' : 'user';
      if (type !== 'doctor' && initial_dependent) {
        input.initial_dependent = initial_dependent;
      }
      if (verification_token) {
        input.verification_token = verification_token;
      }
      // UTMs da sessão: o backend repassa ao evento de ciclo de vida (CRM / listas).
      input.attribution = pickAttribution(getAttributionParams());

      const apiUrl = process.env.REACT_APP_SERVICE_API || 'http://localhost:3200';
      const response = await fetch(`${apiUrl}/api/user/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input: { input } }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errMsg =
          data.error || 'Erro ao cadastrar usuário, verifique os campos e tente novamente.';
        setMessage(errMsg);
        const needsVerification =
          data.code === 'verification_required' || data.code === 'verification_invalid';
        return { success: false, error: errMsg, redirectToVerification: needsVerification };
      }

      const token = data?.access_token;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('email', rest.email?.toLowerCase());
        if (type) {
          localStorage.removeItem('user_register_type');
        }

        const userResponse = await fetchUserInformation(rest.email?.toLowerCase());
        if (userResponse?.data?.users?.length > 0) {
          const user = userResponse?.data?.users[0];
          trackCompleteRegistration({
            userId: user?.id,
            method: 'email',
            email: user?.email,
            phone: user?.telephone,
            phoneDdi: user?.telephone_ddi,
          });
          configUser(user);
        }
        return { success: true };
      }

      setMessage('Erro ao cadastrar usuário, verifique os campos e tente novamente.');
      return { success: false };
    } catch (errors) {
      setMessage('Erro ao cadastrar usuário, verifique os campos e tente novamente.');
      return { success: false };
    }
  };

  const forgotPassword = async (values, { setMessage }) => {
    console.log('🟠 useAuth.forgotPassword - INÍCIO');
    console.log('🟠 useAuth.forgotPassword - values:', values);
    console.log('🟠 useAuth.forgotPassword - setMessage:', typeof setMessage);
    
    try {
      // Formato esperado pelo backend: { input: { input: { email } } }
      const requestData = {
        input: {
          input: {
            email: values.email?.toLowerCase(),
          },
        },
      };
      console.log('🟠 useAuth.forgotPassword - Dados da requisição:', requestData);
      console.log('🟠 useAuth.forgotPassword - Chamando apiForgotPassword...');
      
      const res = await apiForgotPassword(requestData);
      console.log('🟠 useAuth.forgotPassword - Resposta recebida:', res);
      console.log('🟠 useAuth.forgotPassword - res.data:', res?.data);
      console.log('🟠 useAuth.forgotPassword - res.status:', res?.status);
      
      // O axios retorna { data: {...}, status: ... }, então res.data contém o corpo da resposta
      console.log('🟠 useAuth.forgotPassword - Retornando res');
      return res;
    } catch (errors) {
      console.log('🔴 useAuth.forgotPassword - ERRO:', errors);
      console.log('🔴 useAuth.forgotPassword - errors?.response:', errors?.response);
      console.log('🔴 useAuth.forgotPassword - errors?.response?.data:', errors?.response?.data);
      setMessage('Erro ao enviar email, verifique os campos e tente novamente.');
      console.log('🔴 useAuth.forgotPassword - Lançando erro');
      throw errors;
    }
  };

  useEffect(() => {
    if (error) {
      if (error?.message?.includes('Could not verify JWT: JWTExpired')) {
        signOut();
      }
    }
  }, [error]);

  return {
    loading,
    authenticated: token && signedIn,
    signIn,
    signUp,
    forgotPassword,
    signOut,
    extendSession,
    showWarningDialog,
    setShowWarningDialog,
  };
}

export default useAuth;

export const SIGN_IN = gql`
  mutation signIn($input: SignInInput!) {
    signIn(input: $input) {
      access_token
    }
  }
`;

export const SIGN_UP = gql`
  mutation signUp($input: SignUpInput!) {
    signUp(input: $input) {
      access_token
    }
  }
`;

export const GET_USER = gql`
  query getUser($email: String!) {
    users(where: { email: { _eq: $email } }) {
      firebase_uid
      id
      email
      firstname
      lastname
      document
      gender
      gender_identity
      pronouns
      date_of_birth
      zipcode
      address
      number
      complement
      country
      city
      region
      role
      image_url
      telephone
      telephone_ddi
    }
  }
`;

export const CREATE_DOCTOR = gql`
  mutation createDoctor($input: [doctors_insert_input!]!) {
    insert_doctors(objects: $input) {
      affected_rows
    }
  }
`;

export const CREATE_PACIENT = gql`
  mutation createPacient($input: [pacients_insert_input!]!) {
    insert_pacients(objects: $input) {
      affected_rows
    }
  }
`;
